import { Accomplishment } from "../contexts/interfaces";
import { FormData } from "../contexts/interfaces";

export const ACCOMPLISHMENT_MAX_AGE_YEARS = 5;

/** Required Skhole-style accomplishments subject to the 5-year rule. */
export const CORE_ACCOMPLISHMENT_TYPES = [
  "medtheory",
  "medcalculations",
  "cns",
] as const;

export function isCoreAccomplishmentType(type: string): boolean {
  return (CORE_ACCOMPLISHMENT_TYPES as readonly string[]).includes(type);
}

export function getMinSelectableAccomplishmentDate(): Date {
  const minDate = new Date();
  minDate.setFullYear(minDate.getFullYear() - ACCOMPLISHMENT_MAX_AGE_YEARS);
  minDate.setHours(0, 0, 0, 0);
  return minDate;
}

export function isAccomplishmentDateWithinMaxAge(
  date: string | Date | null | undefined
): boolean {
  if (!date) {
    return false;
  }
  const completedAt = new Date(date);
  if (Number.isNaN(completedAt.getTime())) {
    return false;
  }
  return completedAt >= getMinSelectableAccomplishmentDate();
}

export function resolveAccomplishmentDate(
  type: string,
  employeeAccomplishments: Accomplishment[] | undefined,
  options?: {
    checkFormData?: boolean;
    currentData?: FormData;
  }
): { date: string | null; fromAttachment: boolean } {
  let date: string | null = null;
  let fromAttachment = false;

  const employeeMatch = employeeAccomplishments?.find(
    (accomplishment) => accomplishment.type === type
  );
  if (employeeMatch?.date) {
    date = employeeMatch.date;
  }

  if (
    options?.checkFormData &&
    options.currentData?.useAttachments === true &&
    options.currentData?.accomplishments
  ) {
    const formMatch = options.currentData.accomplishments.find(
      (accomplishment) =>
        accomplishment.type === type && accomplishment.fromAttachment === true
    );
    if (formMatch?.date) {
      date = formMatch.date;
      fromAttachment = true;
    }
  }

  return { date, fromAttachment };
}

export function isRequiredAccomplishmentSatisfied(
  type: string,
  date: string | null | undefined
): boolean {
  if (!date) {
    return false;
  }
  if (isCoreAccomplishmentType(type)) {
    return isAccomplishmentDateWithinMaxAge(date);
  }
  return true;
}

export interface AccomplishmentValidationResult {
  valid: boolean;
  missing: { type: string; title: string }[];
  expired: { type: string; title: string }[];
}

export function validateRequiredAccomplishments(
  required: { type: string; title: string }[],
  employeeAccomplishments: Accomplishment[] | undefined,
  options?: {
    checkFormData?: boolean;
    currentData?: FormData;
  }
): AccomplishmentValidationResult {
  const missing: { type: string; title: string }[] = [];
  const expired: { type: string; title: string }[] = [];

  required.forEach((requirement) => {
    const { date } = resolveAccomplishmentDate(
      requirement.type,
      employeeAccomplishments,
      options
    );

    if (!date) {
      missing.push(requirement);
      return;
    }

    if (!isRequiredAccomplishmentSatisfied(requirement.type, date)) {
      expired.push(requirement);
    }
  });

  return {
    valid: missing.length === 0 && expired.length === 0,
    missing,
    expired,
  };
}
